import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { formatNumber, Location } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Indicator } from 'src/app/core/_models/indicator';
import { PdfPictures } from 'src/app/core/_models/pdfPictures';
import { Scenario } from 'src/app/core/_models/scenario';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { CallForTenderService } from 'src/app/core/_services/call-for-tender.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SolutionService } from 'src/app/core/_services/solution.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { BarIndicatorsDetailedBySolutionsComponent } from '../ecopro-results-graphs/bar-indicators-detailed-bySolutions/bar-indicators-detailed-bySolutions.component';
import { BarIndicatorsDetailedByStationsComponent } from '../ecopro-results-graphs/bar-indicators-detailed-byStations/bar-indicators-detailed-byStations.component';
import { BarIndicatorsGlobalByStationsComponent } from '../ecopro-results-graphs/bar-indicators-global-byStations/bar-indicators-global-byStations.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import { DialogCheckScenarioDatasComponent } from '../shared/dialog-check-scenario-datas/dialog-check-scenario-datas.component';

interface TabData {
  title: string;
  code: string;
  value: any;
  enabled: boolean;
  icon: string;
  type: 'standard' | 'special';
}

export type ButtonVisibility = 'visible' | 'hidden' | 'disabled';

export interface ButtonConfig {
  visibility: ButtonVisibility;
  callback?: () => void;
}

export interface SyntheseButtonsConfig {
  generatePdf: ButtonConfig;
  extractExcel: ButtonConfig;
  checkData: ButtonConfig;
}

@Component({
  selector: 'app-synthese',
  templateUrl: './synthese.component.html',
  styleUrls: ['./synthese.component.scss']
})
export class SyntheseComponent implements OnInit, OnChanges {
  @Input() scenario?: Scenario;
  @Input() projectId?: string | null;
  @Input() callForTenderId?: string | null;

  synthese!: any;
  chartPath!: any;
  projectIsRoad?: boolean;
  projectIsEarthwork?: boolean;
  @Input()
  scenarioId?: string;
  @Input()
  hideTitle: boolean = false;
  @Input()
  refreshKey: number = 0;
  @Output()
  loadError = new EventEmitter<void>();
  @Input()
  buttonsConfig: SyntheseButtonsConfig = {
    generatePdf: { visibility: 'visible' },
    extractExcel: { visibility: 'visible' },
    checkData: { visibility: 'visible' }
  };
  private resolvedScenarioId?: string;
  sunburstValuesToGraph!: any;
  energyValues: any;
  coValues: any;
  prValues: any;
  tkValues: any;
  tableIndex: number = 0;
  tabs: TabData[] = [];

  syntheseToGraphValues!: any;
  indicators: Indicator[] = [];
  synthetiqueResultIndicators: Indicator[] = [];
  synthetiqueResultDetailByStation: Indicator[] = [];
  synthetiqueResultDetailByModule: Indicator[] = [];

  COAndEngIndicators: Indicator[] = [];

  @ViewChild(BarIndicatorsGlobalByStationsComponent)
  barIndicatorsGlobalByStationsComponent: BarIndicatorsGlobalByStationsComponent = new BarIndicatorsGlobalByStationsComponent(this.labelI18n);
  @ViewChild(BarIndicatorsDetailedByStationsComponent)
  barIndicatorsDetailedByStationsComponent: BarIndicatorsDetailedByStationsComponent = new BarIndicatorsDetailedByStationsComponent(this.labelI18n);
  @ViewChild(BarIndicatorsDetailedBySolutionsComponent)
  barIndicatorsDetailedBySolutionsComponent: BarIndicatorsDetailedBySolutionsComponent = new BarIndicatorsDetailedBySolutionsComponent(this.labelI18n);

  constructor(
    private projectService: ProjectService,
    private solutionService: SolutionService,
    private acheminementService: AcheminementService,
    private spinnerService: SpinnerOverlayService,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private callForTenderService: CallForTenderService,
    private labelI18n: LabelI18nService
  ) {}

  CONSTANTS = GlobalConstants;
  isExtractingExcel = false;

  goBack(): void {
    this.location.back();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshKey'] && !changes['refreshKey'].firstChange && changes['refreshKey'].currentValue !== changes['refreshKey'].previousValue) {
      if (!this.resolvedScenarioId) {
        const stateScenario = history.state.scenario;
        const stateScenarioId = stateScenario?.scenarioId;
        this.resolvedScenarioId = this.scenario?.scenarioId || this.scenarioId || stateScenarioId;
      }
      if (this.callForTenderId || this.resolvedScenarioId) {
        this.loadSyntheseData();
      }
    }
  }

  private filterSolutions(value: any) {
    return {
      ...value,
      solutions: value?.solutions?.filter((solution: any) => solution && solution.operationTypes && solution.operationTypes.length > 0)
    };
  }

  async ngOnInit() {
    const stateScenario = history.state.scenario;
    if (!this.scenario && stateScenario) {
      this.scenario = stateScenario;
    }
    const stateScenarioId = stateScenario?.scenarioId;
    this.resolvedScenarioId = this.scenario?.scenarioId || this.scenarioId || stateScenarioId;
    if (this.callForTenderId || this.resolvedScenarioId) {
      await this.loadSyntheseData();
    }
    this.chartPath = environment.baseUrl;
  }

  private async loadSyntheseData(): Promise<void> {
    if (!this.callForTenderId && !this.resolvedScenarioId) return;
    this.spinnerService.show();
    try {
      await this.fetchSyntheseData();
    } catch (error) {
      console.error(error);
      this.synthese = undefined;
      this.tabs = [];
      this.syntheseToGraphValues = null;
      this.sunburstValuesToGraph = null;
      this.snackbarService.open(SnackbarTypes.error);
      this.loadError.emit();
    } finally {
      this.spinnerService.hide();
    }
  }

  private async fetchSyntheseData(): Promise<void> {
    this.indicators = [];
    this.synthetiqueResultIndicators = [];
    this.synthetiqueResultDetailByStation = [];
    this.synthetiqueResultDetailByModule = [];
    let tempSyntheseToGraphValues = null;
    let tempSunburstValuesToGraph = null;

    if (this.callForTenderId) {
      const indicatorIds = (this.scenario?.indicatorSelected ?? [])
        .filter((i) => i.isChecked)
        .map((i) => i.indicatorId)
        .filter((id): id is string => !!id);
      if (!indicatorIds.length) {
        this.syntheseToGraphValues = null;
        this.sunburstValuesToGraph = null;
        return;
      }
      const response: any = await this.callForTenderService.getSynthesis(this.callForTenderId, indicatorIds).toPromise();
      this.synthese = response?.synthesis;
      tempSyntheseToGraphValues = response?.graph;
      tempSunburstValuesToGraph = response?.graph;
    } else {
      this.synthese = await this.projectService.getSynthese(this.resolvedScenarioId);
    }

    this.energyValues = this.synthese?.find((item: any) => item.code == 'EN');
    this.coValues = this.synthese?.find((item: any) => item.code == 'CO');
    this.prValues = this.synthese?.find((item: any) => item.code == 'PR');
    this.tkValues = this.synthese?.find((item: any) => item.code == 'TK');

    this.tabs = [
      {
        title: this.translate.instant('indicator_energy'),
        code: 'EN',
        value: this.filterSolutions(this.energyValues),
        enabled: !!this.energyValues,
        icon: 'bolt',
        type: 'standard'
      },
      {
        title: this.translate.instant('indicator_climate_change'),
        code: 'CO',
        value: this.filterSolutions(this.coValues),
        enabled: !!this.coValues,
        icon: 'co2',
        type: 'standard'
      },
      {
        title: this.translate.instant('indicator_resource_preservation'),
        code: 'PR',
        value: this.filterSolutions(this.prValues),
        enabled: !!this.prValues,
        icon: 'grass',
        type: 'standard'
      },
      {
        title: this.translate.instant('indicator_ton_kilometer'),
        code: 'TK',
        value: this.filterSolutions(this.tkValues),
        enabled: !!this.tkValues,
        icon: 'local_shipping',
        type: 'standard'
      }
    ];

    this.synthese?.forEach((item: any) => {
      if (item.code === 'PE') {
        this.tabs.push({
          title: this.translate.instant('indicator_water_preservation'),
          code: 'PE',
          value: item,
          enabled: true,
          icon: 'water',
          type: 'special'
        });
      } else if (item.code === 'PB') {
        this.tabs.push({
          title: this.translate.instant('indicator_biodiversity_preservation'),
          code: 'PB',
          value: item,
          enabled: true,
          icon: 'emoji_nature',
          type: 'special'
        });
      }
    });

    if (!this.callForTenderId) {
      tempSyntheseToGraphValues = await this.projectService.getSyntheseValuesForGraph(this.resolvedScenarioId);
      tempSunburstValuesToGraph = await this.projectService.getSunburstValuesToGraph(this.resolvedScenarioId);
    }

    this.projectIsRoad = this.checkProjectIsRoad(this.synthese);
    this.projectIsEarthwork = this.checkProjectIsEarthwork(this.synthese);
    const tempIndicators: Indicator[] = [];
    const tempSynthetiqueResultIndicators: Indicator[] = [];
    const tempSynthetiqueResultDetailByStation: Indicator[] = [];
    const tempSynthetiqueResultDetailByModule: Indicator[] = [];

    tempSyntheseToGraphValues?.forEach((row: any) => {
      let indicator: Indicator = { isChecked: false };
      indicator.code = row.code;
      indicator.workUnityDisplayShortName = row.shortDisplayName;
      indicator.indicatorId = row.indicatorId;
      indicator.displayName = row.displayName;
      if (indicator.code != 'PE' && indicator.code != 'PB' && indicator.code != 'PR') tempIndicators.push(indicator);

      if (indicator.code != 'PE' && indicator.code != 'PB') tempSynthetiqueResultDetailByModule.push(indicator);

      if (indicator.code == 'EN' || indicator.code == 'CO') tempSynthetiqueResultIndicators.push(indicator);

      if (indicator.code != 'GN' && indicator.code != 'DRC' && indicator.code != 'MR' && indicator.code != 'AE' && indicator.code != 'PE' && indicator.code != 'PB') tempSynthetiqueResultDetailByStation.push(indicator);
    });

    this.indicators = tempIndicators;
    this.synthetiqueResultIndicators = tempSynthetiqueResultIndicators;
    this.synthetiqueResultDetailByStation = tempSynthetiqueResultDetailByStation;
    this.synthetiqueResultDetailByModule = tempSynthetiqueResultDetailByModule;
    this.syntheseToGraphValues = tempSyntheseToGraphValues;
    this.sunburstValuesToGraph = tempSunburstValuesToGraph;
  }

  getSolutionDataSource(solution: any): MatTableDataSource<any> {
    const data = this.flattenSingleSolutionData(solution);
    return new MatTableDataSource(data);
  }

  getDataSource(tabData: TabData): MatTableDataSource<any> {
    if (!tabData.value || !tabData.value.solutions) {
      return new MatTableDataSource([]) as any;
    }

    if (tabData.type === 'special') {
      return new MatTableDataSource(tabData.value.solutions);
    }

    const data = this.flattenSingleSolutionData(tabData.value.solutions);
    return new MatTableDataSource(data);
  }

  getDisplayedColumns(tabData: TabData): string[] {
    if (tabData.type === 'special') {
      return ['icon', 'solution', 'indicator', 'commitment', 'justification'];
    }

    const baseColumns = ['operationType', 'operationName', 'solution'];

    const headerColumns = this.headerValue(tabData.value?.headers) || [];
    return [...baseColumns, ...headerColumns];
  }

  getSpecialIndicatorValue(element: any, code: string): string {
    if (code === 'PE') {
      return element.waterIndicator || '';
    } else if (code === 'PB') {
      return element.biodiversityIndicator || '';
    }
    return '';
  }

  getSpecialJustificationValue(element: any, code: string): string {
    if (code === 'PE') {
      return element.waterFileDescription || this.translate.instant('no_justification');
    } else if (code === 'PB') {
      return element.bioFileDescription || this.translate.instant('no_justification');
    }
    return this.translate.instant('no_justification');
  }

  getSolutionsForTab(tabData: TabData): any[] {
    if (!tabData.value || !tabData.value.solutions) {
      return [];
    }

    return tabData.value.solutions;
  }

  private flattenSingleSolutionData(solution: any): any[] {
    const flatData: any[] = [];

    solution.operationTypes?.forEach((operationType: any) => {
      operationType.operations?.forEach((operation: any) => {
        flatData.push({
          operationType: operationType.operationType,
          operationName: operation.operationName,
          displayName: solution.displayName,
          values: operation.values || {}
        });
      });
    });

    if (flatData.length > 0 && solution.totalValues) {
      flatData.push({
        operationType: 'Total',
        operationName: '',
        displayName: solution.displayName,
        values: solution.totalValues
      });
    }

    return flatData;
  }

  getDisplayedColumnsForSolution(tabData: TabData): string[] {
    const baseColumns = ['operationType', 'operationName'];
    const headerColumns = this.headerValue(tabData.value?.headers) || [];
    return [...baseColumns, ...headerColumns];
  }
  rowValue(obj: any) {
    if (obj) return Object.keys(obj);
    return [];
  }

  getMaxRows(solution?: any) {
    let sum = 0;
    solution?.operationTypes?.forEach((operationType: any) => {
      operationType?.operations?.forEach(() => {
        sum++;
      });
    });
    return sum + 1;
  }

  getHeadersLength(obj: any) {
    if (obj) return Object.values(obj).length;
  }

  headerValue(obj: any): any[] {
    if (obj) return Object.values(obj);
    return [];
  }

  getHeaderKey(rawHeaders: Record<string, string>, header: string) {
    return Object.entries(rawHeaders).find(([_, value]) => value === header)?.[0]!;
  }

  getDisplayValue(code: string, value: any): any {
    if (value === null || value === undefined || value === '') return value;
    const numericValue = Number(value);
    if (!isFinite(numericValue)) return value;
    // Hors CO, le back envoie déjà la notation scientifique ("0.00E+0") : seul le séparateur décimal suit la langue.
    if (code !== 'CO') return this.labelI18n.locale === 'en-US' ? value : String(value).replace('.', ',');
    return formatNumber(numericValue, this.labelI18n.locale, '1.0-2');
  }

  async getAllPictures() {
    this.barIndicatorsGlobalByStationsComponent.synthese = this.syntheseToGraphValues;
    this.barIndicatorsDetailedByStationsComponent.synthese = this.syntheseToGraphValues;
    this.barIndicatorsDetailedBySolutionsComponent.synthese = this.syntheseToGraphValues;

    let pdfPictures: PdfPictures = {
      coCharts: [],
      energyCharts: [],
      tkCharts: [],
      gncharts: [],
      synthetiqueResultENcharts: [],
      synthetiqueResultCOcharts: []
    };
    let specificIndicators = this.synthetiqueResultDetailByModule.filter((x) => x.code == 'CO' || x.code == 'EN' || x.code == 'PR' || x.code == 'TK');

    let specificIndicatorsForSolutions = this.synthetiqueResultDetailByModule.filter((x) => x.code == 'CO' || x.code == 'EN');

    let globalCharts = await this.barIndicatorsGlobalByStationsComponent.generateCharts(specificIndicators);
    let detailedCharts = await this.barIndicatorsDetailedByStationsComponent.generateCharts(specificIndicators);
    let detailedChartsForSolutions = await this.barIndicatorsDetailedBySolutionsComponent.generateCharts(specificIndicatorsForSolutions);

    if (globalCharts?.coCharts?.[0]) {
      pdfPictures.coCharts?.push(globalCharts.coCharts[0]);
    }
    if (detailedCharts && detailedCharts.coCharts) {
      pdfPictures.coCharts?.push(detailedCharts?.coCharts[0]);
    }

    if (globalCharts?.energyCharts?.[0]) {
      pdfPictures.energyCharts?.push(globalCharts.energyCharts[0]);
    }
    if (detailedCharts && detailedCharts.energyCharts) {
      pdfPictures.energyCharts?.push(detailedCharts?.energyCharts[0]);
    }

    if (globalCharts?.tkCharts?.[0]) {
      pdfPictures.tkCharts?.push(globalCharts.tkCharts[0]);
    }
    if (detailedCharts && detailedCharts.tkCharts) {
      pdfPictures.tkCharts?.push(detailedCharts?.tkCharts[0]);
    }

    if (globalCharts?.gncharts?.[0]) {
      pdfPictures.gncharts?.push(globalCharts.gncharts[0]);
    }
    if (detailedCharts && detailedCharts.gncharts) {
      pdfPictures.gncharts?.push(detailedCharts?.gncharts[0]);
    }
    if (detailedChartsForSolutions && detailedChartsForSolutions.synthetiqueResultENcharts) {
      pdfPictures.synthetiqueResultENcharts?.push(detailedChartsForSolutions?.synthetiqueResultENcharts[0]);
    }
    if (detailedChartsForSolutions && detailedChartsForSolutions.synthetiqueResultCOcharts) {
      pdfPictures.synthetiqueResultCOcharts?.push(detailedChartsForSolutions?.synthetiqueResultCOcharts[0]);
    }
    return pdfPictures;
  }

  async clickGeneratePDFEvent() {
    if (!this.resolvedScenarioId) return;
    if (!this.scenario?.indicatorSelected?.some((indicator) => indicator.isChecked)) {
      this.snackbarService.showMessage(this.translate.instant('synthese_pdf_no_indicator'));
      return;
    }
    this.spinnerService.show();
    try {
      let pdfImages = await this.getAllPictures();
      this.projectService.getGeneratePDF(this.resolvedScenarioId, pdfImages).subscribe(
        (data) => {
          if (data.pdfBase64) {
            const byteArray = new Uint8Array(
              atob(data.pdfBase64)
                .split('')
                .map((char) => char.charCodeAt(0))
            );
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const displayName = this.scenario?.displayName || this.translate.instant('synthese_default_name');
            link.setAttribute('download', displayName + ' - Synthèse.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            this.spinnerService.hide();
            this.warnUnreadableLogos(data.pdfUnreadableLogos);
          }
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          this.spinnerService.hide();
        },
        () => {
          this.spinnerService.hide();
        }
      );
    } catch (ex) {
      this.spinnerService.hide();
      this.snackbarService.open(SnackbarTypes.error);
    }
  }

  private warnUnreadableLogos(logos?: string[]) {
    if (!logos?.length) return;
    const labelKeys: { [property: string]: string } = {
      projectLogo: 'project_logo_contractor',
      customerLogo: 'project_logo_customer',
      ueLogo: 'project_logo_company'
    };
    const names = logos.map((logo) => this.translate.instant(labelKeys[logo] ?? logo));
    const key = names.length === 1 ? 'synthese_pdf_unreadable_logo' : 'synthese_pdf_unreadable_logos';
    this.snackbarService.showMessage(this.translate.instant(key, { logos: names.join(', ') }));
  }

  checkProjectIsRoad(synthesis: any) {
    if (!synthesis?.[0]?.solutions?.[0]?.operationTypes) return false;
    return synthesis[0].solutions[0].operationTypes.some((r: { rowTitle: string }) => r.rowTitle == 'Route/VRD');
  }

  checkProjectIsEarthwork(synthesis: any) {
    if (!synthesis?.[0]?.solutions?.[0]?.operationTypes) return false;
    return synthesis[0].solutions[0].operationTypes.some((r: { rowTitle: string }) => r.rowTitle == 'Terrassement');
  }

  clickGenerateExcelEvent() {
    if (!this.resolvedScenarioId) return;
    this.spinnerService.show();
    this.isExtractingExcel = true;
    return this.projectService.getGenerateExcel(this.resolvedScenarioId).subscribe(
      (data) => {
        this.downloadFile(data);
        this.isExtractingExcel = false;
        this.spinnerService.hide();
      },
      () => {
        this.isExtractingExcel = false;
        this.spinnerService.hide();
      },
      () => {
        this.isExtractingExcel = false;
        this.spinnerService.hide();
      }
    );
  }

  downloadFile(data: Blob) {
    const scenarioToUse = this.scenario || history.state.scenario;
    const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([data], { type: contentType });
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    const displayName = this.scenario?.displayName || this.translate.instant('synthese_default_name');
    downloadLink.setAttribute('download', displayName + ' - Synthèse.xlsx');
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  async checkDatas() {
    if (this.scenario?.solutions) {
      const promiseOperations = this.scenario.solutions.map(async (solution) => {
        await this.solutionService
          .get(solution.id!)
          .toPromise()
          .then((solutionDatas) => (solution.operations = solutionDatas.operations));
      });

      await Promise.all(promiseOperations).then(async () => {
        if (this.scenario?.solutions) {
          const promiseJourneys = this.scenario.solutions.map(async (solution) => {
            if (solution.operations)
              solution.operations.forEach((operation) => {
                if (operation.detailedOperation?.inputProducts) {
                  operation.detailedOperation?.inputProducts.forEach(async (inputProduct) => {
                    if (inputProduct.isMultimodal && inputProduct.transportId) {
                      await this.acheminementService
                        .getJourneysForAcheminement(inputProduct.transportId)
                        .toPromise()
                        .then((journeys) => {
                          inputProduct.journeys = journeys;
                        });
                    }
                  });
                }
                if (operation.detailedOperation?.outputProducts) {
                  operation.detailedOperation?.outputProducts.forEach(async (outputProduct) => {
                    if (outputProduct.isMultimodal && outputProduct.transportId) {
                      await this.acheminementService
                        .getJourneysForAcheminement(outputProduct.transportId)
                        .toPromise()
                        .then((journeys) => {
                          outputProduct.journeys = journeys;
                        });
                    }
                  });
                }
              });
          });

          await Promise.all(promiseJourneys).then(() => {
            this.dialog.open(DialogCheckScenarioDatasComponent, {
              data: { scenario: this.scenario },
              autoFocus: false
            });
          });
        }
      });
    }
  }

  onGeneratePdfClick() {
    const config = this.buttonsConfig.generatePdf;
    if (config.callback) {
      config.callback();
    } else {
      this.clickGeneratePDFEvent();
    }
  }

  onExtractExcelClick() {
    const config = this.buttonsConfig.extractExcel;
    if (config.callback) {
      config.callback();
    } else {
      this.clickGenerateExcelEvent();
    }
  }

  onCheckDataClick() {
    const config = this.buttonsConfig.checkData;
    if (config.callback) {
      config.callback();
    } else {
      this.checkDatas();
    }
  }
}
